  class LoadMoreController {
    constructor(container) {
      this.container = container;
      this.loadMoreRoot = container.querySelector('.pagination-load-more');
      if (!this.loadMoreRoot) return;

      var control = this.loadMoreRoot.querySelector('.load-more-control');
      this.button = control ? control.querySelector('.button--show-more') : null;

      // Variant layout: data-page-variant-count > 0 signals we're showing variants as products
      this.isVariantLayout = parseInt(this.loadMoreRoot.getAttribute('data-page-variant-count') || '0', 10) > 0;

      if (this.isVariantLayout) {
        var grid = this._getProductGrid();
        this.variantsPerPage = grid
          ? parseInt(grid.getAttribute('data-variants-per-page') || '12', 10)
          : 12;
        this._initVariantPagination();
      }

      this.updateShownCount();
      this._bindEvents();
    }

    // ─── Variant pagination helpers ────────────────────────────────────────────

    /**
     * On init, hide all variant items beyond the first `variantsPerPage`.
     */
    _initVariantPagination() {
      var grid = this._getProductGrid();
      if (!grid) return;
      Array.prototype.forEach.call(grid.children, function(item, index) {
        if (index >= this.variantsPerPage) {
          item.style.display = 'none';
          item.setAttribute('data-variant-hidden', '');
        }
      }, this);
    }

    /**
     * Reveal up to `count` hidden variant items (in DOM order). Returns how many were revealed.
     */
    _revealUpToCount(count) {
      var grid = this._getProductGrid();
      if (!grid || count <= 0) return 0;
      var hidden = Array.prototype.filter.call(grid.children, function(item) {
        return item.hasAttribute('data-variant-hidden');
      });
      var toReveal = Math.min(count, hidden.length);
      for (var i = 0; i < toReveal; i++) {
        hidden[i].style.display = '';
        hidden[i].removeAttribute('data-variant-hidden');
      }
      return toReveal;
    }

    _hasHiddenVariants() {
      var grid = this._getProductGrid();
      return grid ? grid.querySelector('[data-variant-hidden]') !== null : false;
    }

    _getVisibleCount() {
      var grid = this._getProductGrid();
      if (!grid) return 0;
      if (this.isVariantLayout) {
        return Array.prototype.filter.call(grid.children, function(item) {
          return !item.hasAttribute('data-variant-hidden');
        }).length;
      }
      return grid.children.length;
    }

    /**
     * One user click: show exactly variantsPerPage new cards when possible.
     * If the current server page runs out of hidden variants mid-batch, fetch the next
     * page and keep revealing until the quota is filled or the collection ends.
     */
    _variantLoadMoreClick() {
      var self = this;
      var need = self.variantsPerPage;

      return (async function loop() {
        while (need > 0) {
          var revealed = self._revealUpToCount(need);
          need -= revealed;

          if (need <= 0) break;

          var nextUrl = self.loadMoreRoot.getAttribute('data-next-url');
          if (!nextUrl) break;

          var html = await self._fetchNextPage(nextUrl);
          var parsed = self._extractNextChunk(html);
          var grid = self._getProductGrid();

          parsed.nextItems.forEach(function(node) {
            node.style.display = 'none';
            node.setAttribute('data-variant-hidden', '');
            grid.appendChild(node);
          });

          self.loadMoreRoot.setAttribute('data-current-page', String(parsed.currentPage));
          self.loadMoreRoot.setAttribute('data-total-pages', String(parsed.totalPages));
          self.loadMoreRoot.setAttribute('data-next-url', parsed.nextUrl || '');
        }

        var hasMore = self._hasHiddenVariants() || !!(self.loadMoreRoot.getAttribute('data-next-url'));
        if (!hasMore) {
          self._handleComplete();
        } else {
          self.updateShownCount();
        }
      })();
    }

    // ─── Core methods ──────────────────────────────────────────────────────────

    _bindEvents() {
      if (this.button) {
        this.button.addEventListener('click', () => {
          this._handleClick();
        });
      }
    }

    _getProductGrid() {
      return this.container.querySelector('#product-grid');
    }

    updateShownCount() {
      if (!this.loadMoreRoot) return;
      var statusEl = this.loadMoreRoot.querySelector('.load-more-status');
      if (!statusEl) return;

      var shown = this._getVisibleCount();
      var hasMore = this._hasHiddenVariants() || !!(this.loadMoreRoot.getAttribute('data-next-url'));

      if (this.isVariantLayout) {
        statusEl.textContent = hasMore
          ? String(shown) + ' variants loaded'
          : String(shown) + ' variants total';
      } else {
        var totalItems = parseInt(this.loadMoreRoot.getAttribute('data-total-items') || '0', 10);
        statusEl.textContent = String(shown) + ' of ' + String(totalItems) + ' Products';
      }

      this._updateButtonVisibility(hasMore);
    }

    _updateButtonVisibility(hasMore) {
      if (!this.button) return;
      if (hasMore) {
        this.button.style.display = '';
        this.button.removeAttribute('aria-hidden');
      } else {
        this.button.style.display = 'none';
        this.button.setAttribute('aria-hidden', 'true');
      }
    }

    _setLoading(isLoading) {
      if (this.button) this.button.disabled = !!isLoading;
    }

    _handleComplete() {
      this.loadMoreRoot.setAttribute('data-next-url', '');
      this.updateShownCount();
    }

    _handleClick() {
      if (this.isVariantLayout) {
        this._setLoading(true);
        this._variantLoadMoreClick()
          .then(() => {
            this._setLoading(false);
          })
          .catch(() => {
            this._setLoading(false);
          });
        return;
      }

      var nextUrl = this.loadMoreRoot.getAttribute('data-next-url');
      if (!nextUrl) {
        this._handleComplete();
        return;
      }

      this._setLoading(true);
      this._fetchNextPage(nextUrl)
        .then((html) => {
          var parsed = this._extractNextChunk(html);
          var grid = this._getProductGrid();
          parsed.nextItems.forEach(function(node) { grid.appendChild(node); });
          this.loadMoreRoot.setAttribute('data-current-page', String(parsed.currentPage));
          this.loadMoreRoot.setAttribute('data-total-pages', String(parsed.totalPages));
          this.loadMoreRoot.setAttribute('data-next-url', parsed.nextUrl || '');
          if (!parsed.nextUrl) {
            this._handleComplete();
          } else {
            this.updateShownCount();
            this._setLoading(false);
          }
        })
        .catch(() => {
          this._setLoading(false);
        });
    }

    _fetchNextPage(nextUrl) {
      var url = new URL(nextUrl, window.location.origin);
      return fetch(url.toString(), { credentials: 'same-origin' }).then(function(res) {
        if (!res.ok) throw new Error('Network error');
        return res.text();
      });
    }

    _extractNextChunk(htmlString) {
      var doc = document.implementation.createHTMLDocument('next');
      doc.documentElement.innerHTML = htmlString;
      var nextGrid = doc.querySelector('#product-grid');
      var nextItems = nextGrid ? Array.prototype.slice.call(nextGrid.children) : [];
      var loadMore = doc.querySelector('.pagination-load-more');
      var nextUrl = loadMore ? loadMore.getAttribute('data-next-url') : '';
      var currentPage = loadMore ? parseInt(loadMore.getAttribute('data-current-page') || '1', 10) : NaN;
      var totalPages = loadMore ? parseInt(loadMore.getAttribute('data-total-pages') || '1', 10) : NaN;
      return { nextItems: nextItems, nextUrl: nextUrl, currentPage: currentPage, totalPages: totalPages };
    }
  }

  function init() {
    var containers = document.querySelectorAll('.product-grid-container');
    if (!containers || !containers.length) return;
    Array.prototype.forEach.call(containers, function(container) {
      new LoadMoreController(container);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
